import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import logo from "@/assets/logo.svg";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/where-risk-forms", label: "Where Risk Forms" },
  { to: "/timing", label: "Timing" },
  { to: "/about", label: "About" },
  { to: "/who-this-is-for", label: "Who This Is For" },
];

const Header = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/20 shadow-lg shadow-black/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="hover:opacity-80 transition-opacity duration-300">
          <img src={logo} alt="Boomerang Cultures" className="h-7 md:h-8" width="105" height="32" />
        </Link>

        {/* Desktop */}
        <ul className="hidden md:flex items-center gap-7">
          {navLinks.map(({ to, label }) => (
            <li key={to}>
              <Link
                to={to}
                className={`relative text-[11px] font-medium uppercase tracking-[0.15em] transition-all duration-300 after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-px after:bottom-[-6px] after:left-0 after:bg-primary after:origin-bottom-right after:transition-transform after:duration-500 hover:after:scale-x-100 hover:after:origin-bottom-left ${
                  location.pathname === to
                    ? "text-primary after:scale-x-100"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/book-a-call"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-5 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-warmglow transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-px"
            >
              Book a Call <ArrowRight size={11} />
            </Link>
          </li>
        </ul>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-foreground p-2.5 rounded-full hover:bg-muted/50 transition-colors duration-300"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border/20 px-6 pb-6"
          style={{
            animation: "fadeSlideIn 0.2s ease forwards",
          }}
        >
          <style>{`
            @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
          `}</style>
          <ul className="flex flex-col gap-1">
            {navLinks.map(({ to, label }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  className={`text-[11px] font-medium uppercase tracking-[0.15em] block py-3 smooth ${
                    location.pathname === to
                      ? "text-primary"
                      : "text-muted-foreground"
                  }`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
};

export default Header;
